/**
 * P1.3 Kill choreography — pure math.
 *
 * Replaces the old "linear 900ms opacity fade" with a three-phase arc:
 *
 *   Phase 1 (0 → FLASH_RAMP_MS):     emissive ramps base → white-out
 *   Phase 2 (FLASH_RAMP_MS → FLASH_HOLD_END_MS):
 *                                     hold at white-out, no sag yet
 *   Phase 3 (FLASH_HOLD_END_MS → DURATION_MS):
 *                                     emissive collapses to 0, opacity
 *                                     fades 1 → 0, chassis sags by
 *                                     COLLAPSE_SAG_Y on a cosine ease
 *
 * Returns four channels per frame:
 *   - emissiveBoost  — added to base emissive intensity (chassis
 *                      "blowout" before fade)
 *   - emissiveColorK — 0..1 lerp factor: 0 = base emissive color,
 *                      1 = white-out
 *   - opacity        — 0..1 chassis material opacity
 *   - sagY           — world-Y delta to add to base position (≤ 0)
 *   - visible        — false once fully faded (consumer can skip)
 *
 * Pure functions; no Three deps so we unit-test the curve cleanly.
 */

export const KILL_FLASH_RAMP_MS = 120;
export const KILL_FLASH_HOLD_END_MS = 220;
export const KILL_DURATION_MS = 900;
/** Peak emissive boost during the flash hold (added to base intensity). */
export const KILL_FLASH_PEAK_BOOST = 6.0;
/** World-Y the chassis sinks by between hold end and full fade. */
export const KILL_COLLAPSE_SAG_Y = 0.18;

export interface KillState {
  emissiveBoost: number;
  /** Lerp factor toward white. 0 = base color, 1 = full white. */
  emissiveColorK: number;
  opacity: number;
  sagY: number;
  visible: boolean;
}

const REST: KillState = {
  emissiveBoost: 0,
  emissiveColorK: 0,
  opacity: 1,
  sagY: 0,
  visible: true,
};
const HIDDEN: KillState = {
  emissiveBoost: 0,
  emissiveColorK: 0,
  opacity: 0,
  sagY: -KILL_COLLAPSE_SAG_Y,
  visible: false,
};

/**
 * Returns the per-frame channels for a kill that started `elapsedMs`
 * ago. Pass `null` when not killed (returns rest).
 */
export function killChoreography(elapsedMs: number | null): KillState {
  if (elapsedMs === null || elapsedMs < 0) return REST;
  if (elapsedMs >= KILL_DURATION_MS) return HIDDEN;

  if (elapsedMs < KILL_FLASH_RAMP_MS) {
    // Phase 1 — emissive ramps up. Body still at rest.
    const t = elapsedMs / KILL_FLASH_RAMP_MS;
    return {
      emissiveBoost: KILL_FLASH_PEAK_BOOST * t,
      emissiveColorK: t,
      opacity: 1,
      sagY: 0,
      visible: true,
    };
  }

  if (elapsedMs < KILL_FLASH_HOLD_END_MS) {
    // Phase 2 — peak hold. Pure white-out.
    return {
      emissiveBoost: KILL_FLASH_PEAK_BOOST,
      emissiveColorK: 1,
      opacity: 1,
      sagY: 0,
      visible: true,
    };
  }

  // Phase 3 — collapse and fade. Cosine ease on both emissive decay
  // and sag for a soft, weighty descent.
  const collapseSpan = KILL_DURATION_MS - KILL_FLASH_HOLD_END_MS;
  const u = (elapsedMs - KILL_FLASH_HOLD_END_MS) / collapseSpan; // 0..1
  const ease = 0.5 * (1 - Math.cos(u * Math.PI)); // 0..1, cosine-ease

  return {
    emissiveBoost: KILL_FLASH_PEAK_BOOST * (1 - ease),
    emissiveColorK: 1 - ease,
    opacity: 1 - ease,
    sagY: -KILL_COLLAPSE_SAG_Y * ease,
    visible: ease < 1,
  };
}
